/**
 * Record Audio Element Player
 * Student-facing component with recording capability and playback.
 * Uses onAnswer to submit the recorded audio data URL.
 */

import * as React from 'react';
import { cn, Card, CardContent, Button, Progress } from '@ai-content-hub/content-kit';
import type { ElementPlayerProps } from '@ai-content-hub/content-kit';
import { Mic, Square, Play, Pause, Volume2, Clock, RotateCcw } from 'lucide-react';
import type { RecordAudioElementData } from './schema';
import { recordAudioElementSchema } from './schema';
import { useElementSchemaLocale } from '../i18n/useElementSchemaLocale';

/** Answer is the recorded audio data URL string */
export type RecordAudioAnswer = string;

export interface RecordAudioPlayerProps
    extends ElementPlayerProps<RecordAudioElementData, RecordAudioAnswer> {
    /** When true, suppress the inner data.title heading (e.g. host already shows the title). */
    hideTitle?: boolean;
}

type RecordingState = 'idle' | 'recording' | 'recorded';

/** Format seconds to mm:ss, guarding against Infinity/NaN */
function formatTime(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds < 0) return '00:00';
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Get supported MIME type for MediaRecorder */
function getSupportedMimeType(): string {
    const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
    ];
    for (const type of types) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    return 'audio/webm';
}

export const RecordAudioPlayer: React.FC<RecordAudioPlayerProps> = ({
    data,
    answer,
    onAnswer,
    disabled,
    isReview = false,
    className,
    hideTitle = false,
    config,
    language,
    schema: schemaProp,
}) => {
    // The playable source: either a pre-set src from the editor, or the student's recorded answer
    const audioSrc = answer || data?.src || '';

    const [state, setState] = React.useState<RecordingState>(audioSrc ? 'recorded' : 'idle');
    const [elapsed, setElapsed] = React.useState(0);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [audioLevels, setAudioLevels] = React.useState<number[]>(new Array(32).fill(0));
    const [permissionError, setPermissionError] = React.useState<string | null>(null);

    const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
    const chunksRef = React.useRef<Blob[]>([]);
    const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const analyserRef = React.useRef<AnalyserNode | null>(null);
    const animFrameRef = React.useRef<number | null>(null);
    const streamRef = React.useRef<MediaStream | null>(null);
    const elapsedRef = React.useRef(0);

    const rawMax = data?.maxDuration;
    const maxDuration =
        typeof rawMax === 'number' && Number.isFinite(rawMax) && rawMax >= 10 ? rawMax : 60;

    const schema = schemaProp ?? recordAudioElementSchema;
    const { t } = useElementSchemaLocale(schema.i18n, { config, language });

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
            }
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    // Sync state when answer changes externally
    React.useEffect(() => {
        if (answer) {
            setState('recorded');
        }
    }, [answer]);

    // No data state
    if (!data) {
        return (
            <Card className={cn('record-audio-player-placeholder', className)}>
                <CardContent className="py-3 text-center text-muted-foreground">
                    {t('recordAudioNoData')}
                </CardContent>
            </Card>
        );
    }

    const { title } = data;

    /** Start recording from microphone */
    const startRecording = async () => {
        try {
            setPermissionError(null);
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            // Set up audio analyser for waveform visualization
            const audioContext = new AudioContext();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 64;
            source.connect(analyser);
            analyserRef.current = analyser;

            // Set up MediaRecorder
            const mimeType = getSupportedMimeType();
            const recorder = new MediaRecorder(stream, { mimeType });
            mediaRecorderRef.current = recorder;
            chunksRef.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: mimeType });
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result as string;
                    // Send recorded audio as the answer
                    onAnswer?.(dataUrl);
                    setState('recorded');
                };
                reader.readAsDataURL(blob);

                // Clean up stream
                stream.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
                audioContext.close();
            };

            // Stop any previous playback
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
            setIsPlaying(false);
            setCurrentTime(0);
            setDuration(0);

            recorder.start(100);
            setState('recording');
            setElapsed(0);
            elapsedRef.current = 0;

            // Start timer
            const startTime = Date.now();
            timerRef.current = setInterval(() => {
                const secs = (Date.now() - startTime) / 1000;
                setElapsed(secs);
                elapsedRef.current = secs;

                // Auto-stop at max duration
                if (secs >= maxDuration) {
                    recorder.stop();
                    if (timerRef.current) clearInterval(timerRef.current);
                }
            }, 100);

            // Start waveform animation
            const updateLevels = () => {
                if (!analyserRef.current) return;
                const freqData = new Uint8Array(analyserRef.current.frequencyBinCount);
                analyserRef.current.getByteFrequencyData(freqData);
                const levels = Array.from(freqData).map((v) => v / 255);
                setAudioLevels(levels);
                animFrameRef.current = requestAnimationFrame(updateLevels);
            };
            updateLevels();
        } catch (err) {
            const message =
                err instanceof DOMException && err.name === 'NotAllowedError'
                    ? t('recordAudioMicDenied')
                    : t('recordAudioMicUnavailable');
            setPermissionError(message);
        }
    };

    /** Stop recording */
    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        if (animFrameRef.current) {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = null;
        }
        setAudioLevels(new Array(32).fill(0));
    };

    /** Toggle playback */
    const togglePlayback = () => {
        if (!audioSrc) return;

        if (isPlaying && audioRef.current) {
            audioRef.current.pause();
            setIsPlaying(false);
            return;
        }

        if (!audioRef.current || audioRef.current.src !== audioSrc) {
            const audio = new Audio(audioSrc);
            audioRef.current = audio;

            audio.onloadedmetadata = () => {
                if (Number.isFinite(audio.duration) && audio.duration > 0) {
                    setDuration(audio.duration);
                } else {
                    // WebM/Opus data-URLs often report Infinity duration.
                    // Seek to a very large time to force the browser to resolve
                    // the real duration, then seek back to the start.
                    const onSeeked = () => {
                        audio.removeEventListener('seeked', onSeeked);
                        if (Number.isFinite(audio.duration) && audio.duration > 0) {
                            setDuration(audio.duration);
                        } else {
                            // Last resort: use the recorded elapsed time
                            setDuration(elapsedRef.current || data.duration || 0);
                        }
                        audio.currentTime = 0;
                    };
                    audio.addEventListener('seeked', onSeeked);
                    audio.currentTime = 1e10;
                }
            };
            audio.ontimeupdate = () => {
                setCurrentTime(audio.currentTime);
            };
            audio.onended = () => {
                setIsPlaying(false);
                setCurrentTime(0);
            };
        }

        audioRef.current.play();
        setIsPlaying(true);
    };

    /** Discard recording and reset to idle */
    const discardRecording = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        onAnswer?.('');
        setState('idle');
        setElapsed(0);
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
    };

    const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
        <div className={cn('record-audio-player', className)}>
            <CardContent className="py-3 space-y-3">
                {/* Title */}
                {title && !hideTitle && (
                    <h4 className="record-audio-player-title text-sm font-medium">{title}</h4>
                )}

                {/* Permission Error */}
                {permissionError && (
                    <div className="record-audio-player-error flex items-center gap-2 p-2 rounded-lg bg-destructive/10 text-destructive text-xs">
                        <Mic className="h-3.5 w-3.5 shrink-0" />
                        <span>{permissionError}</span>
                    </div>
                )}

                {/* Read-only / history view with no recording: no recorder UI, just an empty note */}
                {state === 'idle' && (disabled || isReview) && (
                    <div className="record-audio-player-empty py-3 text-center text-xs text-muted-foreground">
                        {t('recordAudioNoData')}
                    </div>
                )}

                {/* Idle State — show record button */}
                {state === 'idle' && !disabled && !isReview && (
                    <div className="record-audio-player-idle flex flex-col items-center gap-3 py-3">
                        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 text-primary">
                            <Mic className="h-7 w-7" />
                        </div>
                        <p className="text-xs text-muted-foreground">
                            {t('recordAudioTapToStart')}
                        </p>
                        <Button
                            onClick={startRecording}
                            disabled={disabled}
                            size="default"
                            className="record-audio-player-start-btn gap-2"
                        >
                            <Mic className="h-4 w-4" />
                            {t('recordAudioStartRecording')}
                        </Button>
                    </div>
                )}

                {/* Recording State */}
                {state === 'recording' && (
                    <div className="record-audio-player-recording flex flex-col items-center gap-3 py-3">
                        {/* Waveform Visualization */}
                        <div className="record-audio-player-waveform flex items-end justify-center gap-[2px] h-12 w-full max-w-xs">
                            {audioLevels.map((level, i) => (
                                <div
                                    key={i}
                                    className="flex-1 rounded-full bg-primary transition-all duration-75"
                                    style={{
                                        height: `${Math.max(4, level * 100)}%`,
                                        opacity: 0.4 + level * 0.6,
                                    }}
                                />
                            ))}
                        </div>

                        {/* Timer */}
                        <div className="record-audio-player-timer flex items-center gap-2 text-base font-mono">
                            <div className="h-2.5 w-2.5 rounded-full bg-destructive animate-pulse" />
                            <span>{formatTime(elapsed)}</span>
                            <span className="text-xs text-muted-foreground">
                                / {formatTime(maxDuration)}
                            </span>
                        </div>

                        {/* Duration Progress */}
                        <Progress
                            value={(elapsed / maxDuration) * 100}
                            className="w-full max-w-xs h-1.5"
                        />

                        {/* Stop Button */}
                        <Button
                            onClick={stopRecording}
                            variant="destructive"
                            size="default"
                            className="record-audio-player-stop-btn gap-2"
                        >
                            <Square className="h-4 w-4" />
                            {t('recordAudioStopRecording')}
                        </Button>
                    </div>
                )}

                {/* Recorded State — Playback + Re-record */}
                {state === 'recorded' && audioSrc && (
                    <div className="record-audio-player-recorded space-y-3">
                        {/* Playback Controls */}
                        <div className="record-audio-player-controls flex items-center gap-3">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={togglePlayback}
                                className="record-audio-player-play-btn h-9 w-9 rounded-full shrink-0"
                            >
                                {isPlaying ? (
                                    <Pause className="h-4 w-4" />
                                ) : (
                                    <Play className="h-4 w-4 ml-0.5" />
                                )}
                            </Button>

                            <div className="flex-1 space-y-1">
                                <Progress
                                    value={progress}
                                    className="record-audio-player-progress h-1.5"
                                />
                                <div className="flex justify-between text-[11px] text-muted-foreground">
                                    <span>{formatTime(currentTime)}</span>
                                    <span className="flex items-center gap-1">
                                        <Clock className="h-3 w-3" />
                                        {formatTime(duration || data.duration || 0)}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Re-record button */}
                        {!disabled && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={discardRecording}
                                className="record-audio-player-rerecord-btn gap-1.5"
                            >
                                <RotateCcw className="h-3.5 w-3.5" />
                                {t('recordAudioRerecord')}
                            </Button>
                        )}
                    </div>
                )}

                {/* Recorded state but no src (cleared) — show idle */}
                {state === 'recorded' && !audioSrc && (
                    <div className="record-audio-player-idle flex flex-col items-center gap-3 py-3">
                        <Volume2 className="h-6 w-6 opacity-40" />
                        <p className="text-xs text-muted-foreground">{t('recordAudioNoRecordingAvailable')}</p>
                        {!disabled && (
                            <Button
                                onClick={startRecording}
                                size="default"
                                className="record-audio-player-start-btn gap-2"
                            >
                                <Mic className="h-4 w-4" />
                                {t('recordAudioStartRecording')}
                            </Button>
                        )}
                    </div>
                )}
            </CardContent>
        </div>
    );
};

export default RecordAudioPlayer;
